
import { useState, useEffect, useCallback } from 'react';
import { useToast } from '@/components/ui/use-toast';

interface GeolocationState {
  loading: boolean;
  error: string | null;
  position: {
    latitude: number;
    longitude: number;
  } | null;
  timestamp: number | null;
}

export const useGeolocation = () => {
  const { toast } = useToast();
  const [state, setState] = useState<GeolocationState>({
    loading: false,
    error: null,
    position: null,
    timestamp: null
  });

  const handleSuccess = useCallback((position: GeolocationPosition) => {
    setState({
      loading: false,
      error: null,
      position: {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
      },
      timestamp: position.timestamp
    });
    
    // Sauvegarder la position dans le localStorage pour une utilisation future
    const positionData = {
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
      timestamp: position.timestamp
    };
    localStorage.setItem('user_last_position', JSON.stringify(positionData));
  }, []);

  const handleError = useCallback((error: GeolocationPositionError) => {
    setState(prev => ({
      ...prev,
      loading: false,
      error: `Erreur de géolocalisation: ${error.message}`,
    }));
    
    toast({
      title: "Impossible d'obtenir votre position",
      description: error.message,
      variant: "destructive"
    });
  }, [toast]);

  // Essayer de charger la dernière position connue depuis le localStorage
  useEffect(() => {
    const savedPosition = localStorage.getItem('user_last_position');
    if (savedPosition) {
      try {
        const parsedPosition = JSON.parse(savedPosition);
        const timestamp = parsedPosition.timestamp;
        const now = Date.now();
        
        // Vérifier si la position n'est pas trop ancienne (moins de 30 minutes)
        if (timestamp && now - timestamp < 30 * 60 * 1000) {
          setState({
            loading: false,
            error: null,
            position: {
              latitude: parsedPosition.latitude,
              longitude: parsedPosition.longitude,
            },
            timestamp
          });
        } else {
          // Si la position est trop ancienne, tenter d'obtenir une nouvelle position
          requestLocation();
        }
      } catch (e) {
        console.error("Erreur lors de la lecture de la position sauvegardée:", e);
      }
    }
  }, []);

  const requestLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: "La géolocalisation n'est pas supportée par votre navigateur.",
      }));
      return;
    }

    setState(prev => ({ ...prev, loading: true, error: null }));
    
    const geoOptions = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0
    };

    navigator.geolocation.getCurrentPosition(
      handleSuccess,
      handleError,
      geoOptions
    );
  }, [handleSuccess, handleError]);

  return {
    ...state,
    requestLocation,
  };
};
